import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import { Button } from '@design/components/button/Button';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { usePostReadAll } from '@terminal/react-query-hooks/events/use-event-alerts/usePostReadAll';
import { useUnreadAlertsCount } from '@terminal/react-query-hooks/events/use-event-alerts/useUnreadAlertsCount';
import { appPaths } from '@terminal/routes/app-paths';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import styles from './Header.module.scss';

export interface HeaderProps {
  activeButtonIndex: number;
  onButtonClick: (index: number) => void;
  onClose: () => void;
  shouldHideTabs: boolean;
}

export const Header = ({
  activeButtonIndex,
  onButtonClick,
  onClose,
  shouldHideTabs,
}: HeaderProps) => {
  const { t } = useTranslation('alerts', { useSuspense: false });
  const navigate = useNavigate();
  const { mutate: readAllAlerts, isPending: isReadAllPending } =
    usePostReadAll();
  const { trackAlertsMarkAllAsRead, trackAlertsPageOpen } = usePosthogEvents();
  const unreadCount = useUnreadAlertsCount();

  const handleNavigateToAlerts = () => {
    trackAlertsPageOpen(POSTHOG_TRACKING_SOURCES.ALERTS_DROPDOWN);
    onClose();
    navigate(appPaths.alerts);
  };

  const buttonLabels = [
    t('alerts:all'),
    t('alerts:unread', { count: unreadCount }),
  ];

  return (
    <div className={styles.headerWrapper}>
      <div className={styles.titleAndButtonRow}>
        <div className={styles.title}>{t('dropdown.title')}</div>
        <div className={styles.buttonContainer}>
          {unreadCount > 0 && (
            <TooltipContainer
              label={
                <div className={styles.tooltipLabel}>
                  {t('alerts:dropdown.markAllAsRead')}
                </div>
              }
            >
              <Button
                variant="empty"
                size="xsmall"
                iconName="CheckDouble"
                disabled={isReadAllPending}
                onClick={() => {
                  readAllAlerts(undefined, {
                    onSuccess: () => {
                      trackAlertsMarkAllAsRead(
                        POSTHOG_TRACKING_SOURCES.ALERTS_DROPDOWN
                      );
                    },
                  });
                }}
              />
            </TooltipContainer>
          )}
          <TooltipContainer
            label={
              <div className={styles.tooltipLabel}>
                {t('alerts:dropdown.viewAllAlerts')}
              </div>
            }
          >
            <Button
              variant="empty"
              size="xsmall"
              iconName="Expand"
              onClick={handleNavigateToAlerts}
            />
          </TooltipContainer>
        </div>
      </div>
      {!shouldHideTabs && (
        <ButtonGroup
          className={styles.styledButtonGroup}
          labels={buttonLabels}
          activeIndex={activeButtonIndex}
          onClick={onButtonClick}
        />
      )}
    </div>
  );
};
